import { useState, useCallback, useRef, useEffect } from 'react';
import { API_V1, fetchApi } from '../config/api';
import { getImageUrl } from '../config/api';
import { refAmount } from '../utils/money';

const PAGE_SIZE = 20;

/**
 * Normalizes a product from the API into a consistent shape for the UI.
 * @param {object} p - Raw product from API
 * @returns {object} Normalized product
 */
function normalizeProduct(p) {
  const priceEur = Number(p.price_eur) || 0;
  return {
    id: p.id ?? p.product_id,
    name: p.name ?? 'Product',
    price: refAmount(p.price_eur),
    price_eur: priceEur,
    description: p.description ?? '',
    image: getImageUrl(p.thumbnail ?? p.thumbnail_url ?? p.image),
    isNew: p.is_new ?? false,
    is_active: p.is_active === true || p.is_active === 1,
  };
}

/**
 * Fetches a single page of products from the API.
 * @param {number} page - Page number (1-based)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @param {{ categoryId?: number, perPage?: number }} options - Optional category_id and per_page
 * @returns {Promise<{ items: object[], hasMore: boolean, total?: number }>}
 */
async function fetchProductsPage(page, signal, options = {}) {
  const { categoryId, perPage = PAGE_SIZE } = options;
  const params = new URLSearchParams();
  params.set('per_page', String(perPage));
  params.set('page', String(page));
  if (categoryId != null) params.set('category_id', String(categoryId));
  const url = `${API_V1}/products?${params.toString()}`;
  const res = await fetchApi(url, {
    signal,
  });
  if (!res.ok) throw new Error('Failed to load products');
  const data = await res.json();
  const list = data.data ?? data;
  const rawItems = Array.isArray(list) ? list : (list?.data ?? []);
  const items = rawItems.map(normalizeProduct).filter((p) => p.id != null);
  const meta = data.meta ?? {};
  const currentPage = meta.current_page ?? data.current_page ?? page;
  const lastPage = meta.last_page ?? data.last_page ?? Math.ceil((meta.total ?? data.total ?? items.length) / perPage);
  const hasMore = currentPage < lastPage;
  const total = meta.total ?? data.total;
  return { items, hasMore, total };
}

/**
 * Infinite scroll hook for products from GET /api/v1/products.
 * @param {{ categoryId?: number, perPage?: number }} options - Optional category_id (e.g. 12 for rolls-mixtos) and per_page (default 20)
 * @returns {{ products: object[], loading: boolean, loadingMore: boolean, error: string|null, hasMore: boolean, total: number|null, loadMore: function, reset: function }}
 */
export function useProductsInfiniteScroll(options = {}) {
  const { categoryId, perPage = PAGE_SIZE } = options;
  const categoryIdRef = useRef(categoryId);
  const perPageRef = useRef(perPage);
  categoryIdRef.current = categoryId;
  perPageRef.current = perPage;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(null);
  const pageRef = useRef(1);
  const abortRef = useRef(null);
  const effectRunIdRef = useRef(0);

  const loadPage = useCallback(async (page, append = false, effectRunId = null) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    const opts = { categoryId: categoryIdRef.current, perPage: perPageRef.current };
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      const { items, hasMore: nextHasMore, total: nextTotal } = await fetchProductsPage(page, signal, opts);
      if (signal.aborted) return;
      if (effectRunId != null && effectRunId !== effectRunIdRef.current) return;
      setProducts((prev) => (append ? [...prev, ...items] : items));
      setHasMore(nextHasMore);
      if (nextTotal != null) setTotal(nextTotal);
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (effectRunId != null && effectRunId !== effectRunIdRef.current) return;
      if (!signal.aborted) {
        setError(err.message || 'Failed to load products');
        if (page === 1) setProducts([]);
      }
    } finally {
      if (effectRunId != null && effectRunId !== effectRunIdRef.current) return;
      if (!signal.aborted) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    loadPage(nextPage, true);
  }, [loading, loadingMore, hasMore, loadPage]);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    pageRef.current = 1;
    setProducts([]);
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setHasMore(true);
    setTotal(null);
  }, []);

  useEffect(() => {
    pageRef.current = 1;
    if (abortRef.current) abortRef.current.abort();
    setProducts([]);
    setLoading(true);
    setError(null);
    setHasMore(true);
    setTotal(null);
    const runId = ++effectRunIdRef.current;
    loadPage(1, false, runId);
    return () => {
      // No abortar aquí: en React Strict Mode el cleanup cancela la petición recién iniciada.
      // Ignoramos la respuesta si el efecto se re-ejecutó (runId ya no coincide).
    };
  }, [categoryId, perPage, loadPage]);

  return {
    products,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore,
    reset,
  };
}

export { PAGE_SIZE };
